import type { User } from 'firebase/auth'
import { RENAME_DATE } from '@shared/brand'
import { isCold } from './perf'

export { RENAME_DATE }
const BRIDGE_MS = 365 * 24 * 60 * 60 * 1000

const AUDIENCE_KEY = 'beacon_rename_audience'
const NOTICE_KEY = 'beacon_rename_notice'
const SHOWS_KEY = 'beacon_rename_notice_shows'
// Launches the card may be seen in before it retires on its own, "Got it" or not.
const MAX_SHOWS = 3
// The current launch already counted as one; the card stays up for the rest of it.
let shownThisLaunch = false

export function isRenameBridgeActive(now = Date.now()): boolean {
  return now < RENAME_DATE + BRIDGE_MS
}

// Decided once per device, on the first boot of a renamed build: a device that
// already had beacon_visited before this boot used Beacon. Persisted because
// from its second boot a brand-new visitor has beacon_visited too.
function isLegacyDevice(): boolean {
  let audience = localStorage.getItem(AUDIENCE_KEY)
  if (!audience) {
    audience = isCold ? 'new' : 'legacy'
    localStorage.setItem(AUDIENCE_KEY, audience)
  }
  return audience === 'legacy'
}

function isLegacyAccount(user: User | null): boolean {
  const created = user && !user.isAnonymous ? Date.parse(user.metadata.creationTime ?? '') : NaN
  return created < RENAME_DATE
}

export function shouldShowRenameNotice(user: User | null): boolean {
  if (!isRenameBridgeActive()) return false
  try {
    if (localStorage.getItem(NOTICE_KEY) === 'seen') return false
    if (!shownThisLaunch && Number(localStorage.getItem(SHOWS_KEY) ?? 0) >= MAX_SHOWS) return false
    return isLegacyDevice() || isLegacyAccount(user)
  } catch {
    // Blocked storage can't remember a dismissal, so the card would return every launch.
    return false
  }
}

export function markRenameNoticeSeen() {
  try {
    localStorage.setItem(NOTICE_KEY, 'seen')
  } catch {
    // Unreachable in practice: shouldShowRenameNotice already proved storage works.
  }
}

// Called when the card is actually on screen; counts at most once per launch.
export function recordRenameNoticeShown() {
  if (shownThisLaunch) return
  shownThisLaunch = true
  try {
    localStorage.setItem(SHOWS_KEY, String(Number(localStorage.getItem(SHOWS_KEY) ?? 0) + 1))
  } catch {
    // Unreachable in practice: shouldShowRenameNotice already proved storage works.
  }
}
